/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import {loadModules} from 'esri-loader';
import isArray from 'lodash.isarray';

/**
 * Provides a hook for calculating a map extent based on a set of points.
 *
 * ```typescript
 *
 *    const zoomTo = useZoomTo();
 *    const extent = zoomTo?.current(points, 1.2);
 *
 * ```
 */
export const useZoomTo = () => {
  const zoomTo = useRef(null);

  useEffect(() => {
    loadModules(['esri/geometry/Point', 'esri/geometry/Multipoint', 'esri/geometry/SpatialReference'])
      .then(([Point, Multipoint, SpatialReference]) => {
        zoomTo.current = function(points: any[], factor: number): __esri.Extent {
          const multipoint = new Multipoint(new SpatialReference({wkid: 4326}));
          points?.forEach(p => {
            const coordinates = isArray(p) ? p : (p?.geometry?.coordinates || p?.coordinates);
            multipoint.addPoint(new Point(coordinates[0], coordinates[1]));
          });
          return multipoint.extent ? multipoint.extent.expand(factor) : null;
        };
      });
  }, [zoomTo]);

  return function(points: any[], factor: number): __esri.Extent {
    return zoomTo?.current ? zoomTo.current(points, factor) : null;
  };
};

export default useZoomTo;
